import React, { useState, useEffect } from 'react';
import { Archive, X, Check, Clipboard, Hand, MapPin, Send } from 'lucide-react';
import inventoryService from '../services/inventoryService';
import '../styles/inventory.css';

const CATEGORIES = [
  "Elektronik",
  "El Aletleri",
  "Bilgisayar ve Yazılım",
  "Malzeme",
  "Mobilya",
  "Diğer"
];

const STATUS_LABELS = {
  pending: 'Onay Bekliyor',
  active: 'Onaylandı (Sizde)',
  returned: 'İade Edildi',
  rejected: 'Reddedildi'
};

const today = () => new Date().toISOString().split('T')[0];

const emptyRequestForm = {
  itemId: '',
  itemName: '',
  borrowerName: '',
  borrowerPhone: '',
  department: '',
  quantity: 1,
  expectedReturnDate: '',
  borrowCondition: 'İyi',
  borrowPhotos: [],
  notes: ''
};

/**
 * Kullanıcının kendisinin doldurduğu "Ödünç Alma Talebi" sayfası.
 * Talepler yönetici onayına düşer; sonuç bildirim zili ile kullanıcıya iletilir.
 */
const BorrowRequestPage = () => {
  const [activeTab, setActiveTab] = useState('catalog');
  const [items, setItems] = useState([]);
  const [myBorrows, setMyBorrows] = useState([]);
  const [locationGroups, setLocationGroups] = useState([]);
  const [filters, setFilters] = useState({ search: '', category: '', location: '' });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [requestForm, setRequestForm] = useState(emptyRequestForm);

  useEffect(() => {
    inventoryService.getLocations()
      .then(res => setLocationGroups(res.groups || []))
      .catch(err => console.error('Konumlar alınamadı:', err));
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'catalog') {
        const response = await inventoryService.getCatalog(filters);
        setItems(response.items || []);
      } else {
        const response = await inventoryService.getMyBorrows();
        setMyBorrows(response.borrows || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openRequestModal = (item) => {
    setSelectedItem(item);
    setRequestForm({ ...emptyRequestForm, itemId: item._id, itemName: item.name });
  };

  const closeRequestModal = () => {
    setSelectedItem(null);
    setRequestForm(emptyRequestForm);
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setError('');
    try {
      const urls = [];
      for (const file of files) {
        const response = await inventoryService.uploadPhoto(file);
        urls.push(response.url);
      }
      setRequestForm(prev => ({ ...prev, borrowPhotos: [...prev.borrowPhotos, ...urls] }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const removePhoto = (url) => {
    setRequestForm(prev => ({ ...prev, borrowPhotos: prev.borrowPhotos.filter(p => p !== url) }));
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!requestForm.borrowerName.trim()) {
      setError('Lütfen adınızı ve soyadınızı yazın');
      return;
    }
    if (!requestForm.quantity || requestForm.quantity < 1 || requestForm.quantity > selectedItem.availableQuantity) {
      setError(`Miktar 1 ile ${selectedItem.availableQuantity} arasında olmalı`);
      return;
    }
    if (!requestForm.expectedReturnDate) {
      setError('Lütfen beklenen iade tarihini seçin');
      return;
    }
    if (requestForm.borrowPhotos.length === 0) {
      setError('Lütfen malzemenin en az bir fotoğrafını yükleyin');
      return;
    }

    setSubmitting(true);
    try {
      await inventoryService.createBorrowRequest(requestForm);
      setSuccess('Talebiniz yöneticiye iletildi. Onaylandığında veya reddedildiğinde bildirim alacaksınız.');
      closeRequestModal();
      setActiveTab('mine');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1><Hand className="inline-icon"/> Malzeme Ödünç Talebi</h1>
        <p>İhtiyacınız olan malzemeyi seçin, formu doldurun; talebiniz yönetici onayına gönderilir.</p>
      </div>

      {error && (
        <div className="notification error">
          <span><X className="inline-icon"/> {error}</span>
          <button onClick={() => setError('')}><X /></button>
        </div>
      )}

      {success && (
        <div className="notification success">
          <span><Check className="inline-icon"/> {success}</span>
          <button onClick={() => setSuccess('')}><X /></button>
        </div>
      )}

      <div className="inventory-tabs">
        <button
          className={activeTab === 'catalog' ? 'active' : ''}
          onClick={() => setActiveTab('catalog')}
        >
          <Archive className="inline-icon"/> Malzemeler
        </button>
        <button
          className={activeTab === 'mine' ? 'active' : ''}
          onClick={() => setActiveTab('mine')}
        >
          <Clipboard className="inline-icon"/> Taleplerim
        </button>
      </div>

      <div className="inventory-content">
        {activeTab === 'catalog' && (
          <>
            <div className="filters">
              <input
                type="text"
                placeholder="Malzeme ara..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                <option value="">Tüm Kategoriler</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              >
                <option value="">Tüm Konumlar</option>
                {locationGroups.map(group => (
                  <optgroup key={group.group} label={group.group}>
                    {group.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="loading">Yükleniyor...</div>
            ) : items.length === 0 ? (
              <div className="loading">Malzeme bulunamadı</div>
            ) : (
              <div className="catalog-grid">
                {items.map(item => (
                  <div key={item._id} className="catalog-card">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} />
                    ) : (
                      <div className="no-image"><Archive /></div>
                    )}
                    <div className="catalog-card-body">
                      <h3>{item.name}</h3>
                      <span className="small-text">{item.category}</span>
                      {item.location && (
                        <span className="small-text"><MapPin size={14} className="inline-icon"/> {item.location}</span>
                      )}
                      <span>
                        Müsait: <strong>{item.availableQuantity}</strong> {item.unit || 'Adet'}
                      </span>
                      <button
                        className="btn-primary"
                        disabled={item.availableQuantity < 1}
                        onClick={() => openRequestModal(item)}
                      >
                        {item.availableQuantity < 1 ? 'Şu an müsait değil' : 'Talep Et'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'mine' && (
          loading ? (
            <div className="loading">Yükleniyor...</div>
          ) : myBorrows.length === 0 ? (
            <div className="loading">Henüz talebiniz yok</div>
          ) : (
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Malzeme</th>
                  <th>Miktar</th>
                  <th>Talep Tarihi</th>
                  <th>Beklenen İade</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {myBorrows.map(borrow => (
                  <tr key={borrow._id}>
                    <td data-label="Malzeme">{borrow.itemName}</td>
                    <td data-label="Miktar">{borrow.quantity}</td>
                    <td data-label="Talep Tarihi">{new Date(borrow.createdAt).toLocaleDateString('tr-TR')}</td>
                    <td data-label="Beklenen İade">{new Date(borrow.expectedReturnDate).toLocaleDateString('tr-TR')}</td>
                    <td data-label="Durum">
                      <span className={`status-badge ${borrow.status}`}>
                        {STATUS_LABELS[borrow.status] || borrow.status}
                      </span>
                      {borrow.rejectionReason && (
                        <div className="small-text">Sebep: {borrow.rejectionReason}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {selectedItem && (
        <div className="modal-overlay" onClick={closeRequestModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ödünç Talebi: {selectedItem.name}</h2>
              <button onClick={closeRequestModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Ad Soyad *</label>
                  <input
                    type="text"
                    value={requestForm.borrowerName}
                    onChange={(e) => setRequestForm({ ...requestForm, borrowerName: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={requestForm.borrowerPhone}
                    onChange={(e) => setRequestForm({ ...requestForm, borrowerPhone: e.target.value })}
                    placeholder="05551234567"
                  />
                </div>

                <div className="form-group">
                  <label>Birim</label>
                  <input
                    type="text"
                    value={requestForm.department}
                    onChange={(e) => setRequestForm({ ...requestForm, department: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Miktar * (müsait: {selectedItem.availableQuantity})</label>
                  <input
                    type="number"
                    value={requestForm.quantity}
                    onChange={(e) => setRequestForm({ ...requestForm, quantity: parseInt(e.target.value) || '' })}
                    min="1"
                    max={selectedItem.availableQuantity}
                  />
                </div>

                <div className="form-group">
                  <label>Beklenen İade Tarihi *</label>
                  <input
                    type="date"
                    value={requestForm.expectedReturnDate}
                    min={today()}
                    onChange={(e) => setRequestForm({ ...requestForm, expectedReturnDate: e.target.value })}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Kullanım Amacı / Not</label>
                  <textarea
                    value={requestForm.notes}
                    onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                    rows="2"
                    placeholder="Malzemeyi ne için kullanacağınızı kısaca yazın"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Malzemenin Fotoğrafı * (teslim alırkenki hali)</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    disabled={uploading}
                  />
                  {uploading && <span className="small-text">Yükleniyor...</span>}
                  <div className="photo-grid">
                    {requestForm.borrowPhotos.map((url, index) => (
                      <div key={url} className="photo-item">
                        <img src={url} alt={`Fotoğraf ${index + 1}`} />
                        <button onClick={() => removePhoto(url)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeRequestModal}>
                İptal
              </button>
              <button className="btn-primary" onClick={handleSubmit} disabled={submitting || uploading}>
                <Send className="inline-icon"/> {submitting ? 'Gönderiliyor...' : 'Talebi Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BorrowRequestPage;
